import { NO, UITextField, UIView, YES } from "uicore-ts"
import { RHUsageEvent, RHUsageEventRuntime } from "./RHUsageEventRuntime"


export interface RHWorkflowRecordingMetadata {
    origin: string
    pathname: string
    userAgent: string
    language: string
    viewport: {
        width: number
        height: number
    }
    timezoneOffsetMinutes: number
}

export interface RHWorkflowRecording {
    schemaVersion: number
    id?: string
    name: string
    startedAt: string
    finishedAt?: string
    options: RHWorkflowRecordingOptions
    metadata: RHWorkflowRecordingMetadata
    externalTimeline?: RHWorkflowRecordingExternalTimeline
    externalComments: RHWorkflowRecordingExternalComment[]
    events: RHUsageEvent[]
}

export interface RHWorkflowRecordingOptions {
    includePointerDetails: boolean
    includeFocusEvents: boolean
    includeKeyboardDetails: boolean
    includeControlDuplicates: boolean
}

export interface RHWorkflowRecordingFileSaveResult {
    success: boolean
    fileName: string
    relativePath: string
    message?: string
}

export interface RHWorkflowRecordingExternalTimeline {
    id: string
    source: string
    title?: string
    startedAtEpochMs: number
    timeOriginEpochMs?: number
    videoUrl?: string
    googleDocUrl?: string
    googleDocId?: string
    notes?: string
}

export interface RHWorkflowRecordingExternalComment {
    id: string
    timelineId?: string
    createdAtEpochMs: number
    videoTimeMs?: number
    recordingTimeMs?: number
    kind?: string
    text: string
    googleDocUrl?: string
    googleDocAnchorUrl?: string
}

export interface RHWorkflowRecordingPluginBridgeSnapshot {
    isRecording: boolean
    recording: RHWorkflowRecording
    savedRecordings: RHWorkflowRecording[]
    externalTimeline?: RHWorkflowRecordingExternalTimeline
    externalComments: RHWorkflowRecordingExternalComment[]
}

export interface RHWorkflowRecordingRuntimeInterface {
    install: () => void
    beginRecording: (name?: string, options?: Partial<RHWorkflowRecordingOptions>) => void
    finishRecording: () => RHWorkflowRecording
    clearRecording: () => void
    recordedEvents: () => RHUsageEvent[]
    recording: () => RHWorkflowRecording
    recordingOptions: () => RHWorkflowRecordingOptions
    updateRecordingOptions: (options: Partial<RHWorkflowRecordingOptions>) => RHWorkflowRecordingOptions
    registerExternalTimeline: (timeline: RHWorkflowRecordingExternalTimeline) => RHWorkflowRecordingExternalTimeline
    updateExternalTimeline: (timeline: Partial<RHWorkflowRecordingExternalTimeline>) => RHWorkflowRecordingExternalTimeline | undefined
    clearExternalTimeline: () => void
    externalTimeline: () => RHWorkflowRecordingExternalTimeline | undefined
    addExternalComment: (comment: Partial<RHWorkflowRecordingExternalComment> & { text: string }) => RHWorkflowRecordingExternalComment
    externalComments: () => RHWorkflowRecordingExternalComment[]
    pluginBridgeSnapshot: () => RHWorkflowRecordingPluginBridgeSnapshot
    saveCurrentRecording: () => RHWorkflowRecording
    savedRecordings: () => RHWorkflowRecording[]
    deleteSavedRecording: (recordingID: string) => void
    clearSavedRecordings: () => void
    saveCurrentRecordingToTestRecordings: () => Promise<RHWorkflowRecordingFileSaveResult>
    saveRecordingToTestRecordings: (recording: RHWorkflowRecording) => Promise<RHWorkflowRecordingFileSaveResult>
    downloadRecording: () => RHWorkflowRecording
    isRecording: () => boolean
    fileSafeName: (name: string) => string
}

declare global {
    interface Window {
        RHWorkflowRecordingRuntime?: RHWorkflowRecordingRuntimeInterface
    }
}


// What the recording browser plugin sends with a command; which fields are present depends on the command.
type RHWorkflowPluginPayload = Partial<RHWorkflowRecordingOptions> & Partial<RHWorkflowRecordingExternalTimeline> & {
    text?: string
    name?: string
    options?: Partial<RHWorkflowRecordingOptions>
    recordingID?: string
    id?: string
}


export class RHWorkflowRecordingRuntime {
    
    static _isInstalled = NO
    static _isRecording = NO
    static _recordingName = "workflow-recording"
    static _recordingID = ""
    static _startedAt = ""
    static _finishedAt = ""
    static _recordingOptions: RHWorkflowRecordingOptions = RHWorkflowRecordingRuntime.defaultRecordingOptions()
    static _events: RHUsageEvent[] = []
    static _externalTimeline: RHWorkflowRecordingExternalTimeline | undefined
    static _externalComments: RHWorkflowRecordingExternalComment[] = []
    static _eventSink = (event: RHUsageEvent) => RHWorkflowRecordingRuntime._usageEventWasEmitted(event)
    static _pluginMessageHandler = (event: MessageEvent) => RHWorkflowRecordingRuntime._pluginMessageWasReceived(event)
    static readonly _savedRecordingsStorageKey = "RHWorkflowRecordingRuntime.savedRecordings.v1"
    static readonly _maximumDisplayTextLength = 180
    static readonly _pointerDetailControlEventKeys = new Set<string>([
        UIView.controlEvent.PointerDown,
        UIView.controlEvent.PointerMove,
        UIView.controlEvent.PointerDrag,
        UIView.controlEvent.PointerLeave,
        UIView.controlEvent.PointerEnter,
        UIView.controlEvent.PointerUp,
        UIView.controlEvent.MultipleTouches,
        UIView.controlEvent.PointerCancel,
        UIView.controlEvent.PointerHover,
        UIView.controlEvent.PointerTap,
        UIView.controlEvent.ScrollWheel
    ])
    static readonly _keyboardDetailControlEventKeys = new Set<string>([
        UIView.controlEvent.EnterUp,
        UIView.controlEvent.SpaceDown
    ])
    static readonly _focusControlEventKeys = new Set<string>([
        UIView.controlEvent.Focus,
        UIView.controlEvent.Blur
    ])
    static readonly _duplicateControlEventKeys = new Set<string>([
        UITextField.controlEvent.TextChange,
        UITextField.controlEvent.ValidationChange
    ])
    
    static install() {
        
        if (RHWorkflowRecordingRuntime._isInstalled) {
            return
        }
        
        RHWorkflowRecordingRuntime._isInstalled = YES
        RHUsageEventRuntime.install()
        RHUsageEventRuntime.addEventSink(RHWorkflowRecordingRuntime._eventSink)
        
        window.RHWorkflowRecordingRuntime = {
            install: () => RHWorkflowRecordingRuntime.install(),
            beginRecording: (name, options) => RHWorkflowRecordingRuntime.beginRecording(name, options),
            finishRecording: () => RHWorkflowRecordingRuntime.finishRecording(),
            clearRecording: () => RHWorkflowRecordingRuntime.clearRecording(),
            recordedEvents: () => RHWorkflowRecordingRuntime.recordedEvents(),
            recording: () => RHWorkflowRecordingRuntime.recording(),
            recordingOptions: () => RHWorkflowRecordingRuntime.recordingOptions(),
            updateRecordingOptions: options => RHWorkflowRecordingRuntime.updateRecordingOptions(options),
            registerExternalTimeline: timeline => RHWorkflowRecordingRuntime.registerExternalTimeline(timeline),
            updateExternalTimeline: timeline => RHWorkflowRecordingRuntime.updateExternalTimeline(timeline),
            clearExternalTimeline: () => RHWorkflowRecordingRuntime.clearExternalTimeline(),
            externalTimeline: () => RHWorkflowRecordingRuntime.externalTimeline(),
            addExternalComment: comment => RHWorkflowRecordingRuntime.addExternalComment(comment),
            externalComments: () => RHWorkflowRecordingRuntime.externalComments(),
            pluginBridgeSnapshot: () => RHWorkflowRecordingRuntime.pluginBridgeSnapshot(),
            saveCurrentRecording: () => RHWorkflowRecordingRuntime.saveCurrentRecording(),
            savedRecordings: () => RHWorkflowRecordingRuntime.savedRecordings(),
            deleteSavedRecording: recordingID => RHWorkflowRecordingRuntime.deleteSavedRecording(recordingID),
            clearSavedRecordings: () => RHWorkflowRecordingRuntime.clearSavedRecordings(),
            saveCurrentRecordingToTestRecordings: () => RHWorkflowRecordingRuntime.saveCurrentRecordingToTestRecordings(),
            saveRecordingToTestRecordings: recording => RHWorkflowRecordingRuntime.saveRecordingToTestRecordings(recording),
            downloadRecording: () => RHWorkflowRecordingRuntime.downloadRecording(),
            isRecording: () => RHWorkflowRecordingRuntime.isRecording(),
            fileSafeName: name => RHWorkflowRecordingRuntime.fileSafeName(name)
        }
        
        // project-rules-ignore no-add-event-listener
        window.addEventListener("message", RHWorkflowRecordingRuntime._pluginMessageHandler)
        
        RHWorkflowRecordingRuntime._beginRecordingFromURLIfNeeded()
        
    }
    
    static beginRecording(name?: string, options?: Partial<RHWorkflowRecordingOptions>) {
        
        RHWorkflowRecordingRuntime.clearRecording()
        RHUsageEventRuntime.resetSanitizedJSONSession()
        RHWorkflowRecordingRuntime._recordingName = RHWorkflowRecordingRuntime._recordingNameFromName(name)
        if (options) {
            RHWorkflowRecordingRuntime.updateRecordingOptions(options)
        }
        RHWorkflowRecordingRuntime._recordingID = RHWorkflowRecordingRuntime._newRecordingID()
        RHWorkflowRecordingRuntime._startedAt = new Date().toISOString()
        RHWorkflowRecordingRuntime._finishedAt = ""
        RHWorkflowRecordingRuntime._isRecording = YES
        RHUsageEventRuntime.registerMilestone("Recording started")
        RHWorkflowRecordingRuntime._emitRecordingDidChange("beginRecording")
        
    }
    
    static finishRecording(): RHWorkflowRecording {
        
        if (RHWorkflowRecordingRuntime._isRecording === YES) {
            RHUsageEventRuntime.registerMilestone("Recording finished")
            RHWorkflowRecordingRuntime._finishedAt = new Date().toISOString()
            RHWorkflowRecordingRuntime._isRecording = NO
            RHWorkflowRecordingRuntime._emitRecordingDidChange("finishRecording")
        }
        
        return RHWorkflowRecordingRuntime.recording()
        
    }
    
    static clearRecording() {
        
        RHWorkflowRecordingRuntime._events = []
        RHWorkflowRecordingRuntime._externalComments = []
        RHWorkflowRecordingRuntime._emitRecordingDidChange("clearRecording")
        
    }
    
    static recordedEvents(): RHUsageEvent[] {
        
        return RHWorkflowRecordingRuntime._events.slice()
        
    }
    
    static recording(): RHWorkflowRecording {
        
        return {
            schemaVersion: 1,
            id: RHWorkflowRecordingRuntime._recordingID,
            name: RHWorkflowRecordingRuntime._recordingName,
            startedAt: RHWorkflowRecordingRuntime._startedAt,
            finishedAt: RHWorkflowRecordingRuntime._finishedAt || undefined,
            options: RHWorkflowRecordingRuntime.recordingOptions(),
            metadata: RHWorkflowRecordingRuntime.metadata(),
            externalTimeline: RHWorkflowRecordingRuntime._externalTimeline,
            externalComments: RHWorkflowRecordingRuntime.externalComments(),
            events: RHWorkflowRecordingRuntime.recordedEvents()
        }
        
    }
    
    static defaultRecordingOptions(): RHWorkflowRecordingOptions {
        
        return {
            includePointerDetails: NO,
            includeFocusEvents: NO,
            includeKeyboardDetails: NO,
            includeControlDuplicates: NO
        }
        
    }
    
    static recordingOptions(): RHWorkflowRecordingOptions {
        
        return { ...RHWorkflowRecordingRuntime._recordingOptions }
        
    }
    
    static updateRecordingOptions(options: Partial<RHWorkflowRecordingOptions>): RHWorkflowRecordingOptions {
        
        RHWorkflowRecordingRuntime._recordingOptions = {
            ...RHWorkflowRecordingRuntime._recordingOptions,
            ...options
        }
        RHWorkflowRecordingRuntime._emitRecordingDidChange("updateRecordingOptions")
        return RHWorkflowRecordingRuntime.recordingOptions()
        
    }
    
    static registerExternalTimeline(timeline: RHWorkflowRecordingExternalTimeline): RHWorkflowRecordingExternalTimeline {
        
        RHWorkflowRecordingRuntime._externalTimeline = {
            ...timeline,
            id: timeline.id || RHWorkflowRecordingRuntime._newRecordingID(),
            startedAtEpochMs: timeline.startedAtEpochMs || Date.now(),
            source: timeline.source || "browser-plugin"
        }
        RHWorkflowRecordingRuntime._emitRecordingDidChange("registerExternalTimeline")
        return RHWorkflowRecordingRuntime._externalTimeline
        
    }
    
    static updateExternalTimeline(
        timeline: Partial<RHWorkflowRecordingExternalTimeline>
    ): RHWorkflowRecordingExternalTimeline | undefined {
        
        if (!RHWorkflowRecordingRuntime._externalTimeline) {
            if (timeline.id) {
                return RHWorkflowRecordingRuntime.registerExternalTimeline({
                    id: timeline.id,
                    source: timeline.source || "browser-plugin",
                    startedAtEpochMs: timeline.startedAtEpochMs || Date.now(),
                    title: timeline.title,
                    timeOriginEpochMs: timeline.timeOriginEpochMs,
                    videoUrl: timeline.videoUrl,
                    googleDocUrl: timeline.googleDocUrl,
                    googleDocId: timeline.googleDocId,
                    notes: timeline.notes
                })
            }
            return undefined
        }
        
        RHWorkflowRecordingRuntime._externalTimeline = {
            ...RHWorkflowRecordingRuntime._externalTimeline,
            ...timeline
        }
        RHWorkflowRecordingRuntime._emitRecordingDidChange("updateExternalTimeline")
        return RHWorkflowRecordingRuntime._externalTimeline
        
    }
    
    static clearExternalTimeline() {
        
        RHWorkflowRecordingRuntime._externalTimeline = undefined
        RHWorkflowRecordingRuntime._emitRecordingDidChange("clearExternalTimeline")
        
    }
    
    static externalTimeline(): RHWorkflowRecordingExternalTimeline | undefined {
        
        return RHWorkflowRecordingRuntime._externalTimeline
        
    }
    
    static addExternalComment(
        comment: Partial<RHWorkflowRecordingExternalComment> & { text: string }
    ): RHWorkflowRecordingExternalComment {
        
        const createdAtEpochMs = comment.createdAtEpochMs || Date.now()
        const normalizedComment: RHWorkflowRecordingExternalComment = {
            id: comment.id || RHWorkflowRecordingRuntime._newRecordingID(),
            timelineId: comment.timelineId || RHWorkflowRecordingRuntime._externalTimeline?.id,
            createdAtEpochMs,
            videoTimeMs: RHWorkflowRecordingRuntime._videoTimeMsForComment(comment, createdAtEpochMs),
            recordingTimeMs: RHWorkflowRecordingRuntime._recordingTimeMsForComment(comment, createdAtEpochMs),
            kind: comment.kind || "note",
            text: comment.text,
            googleDocUrl: comment.googleDocUrl || RHWorkflowRecordingRuntime._externalTimeline?.googleDocUrl,
            googleDocAnchorUrl: comment.googleDocAnchorUrl
        }
        
        RHWorkflowRecordingRuntime._externalComments.push(normalizedComment)
        RHWorkflowRecordingRuntime._events.push({
            kind: "external-comment",
            timestamp: normalizedComment.createdAtEpochMs,
            message: normalizedComment.text,
            externalTimelineID: normalizedComment.timelineId,
            externalCommentID: normalizedComment.id,
            videoTimeMs: normalizedComment.videoTimeMs,
            recordingTimeMs: normalizedComment.recordingTimeMs,
            externalDocumentURL: normalizedComment.googleDocAnchorUrl || normalizedComment.googleDocUrl
        })
        RHWorkflowRecordingRuntime._emitRecordingDidChange("addExternalComment")
        return normalizedComment
        
    }
    
    static externalComments(): RHWorkflowRecordingExternalComment[] {
        
        return RHWorkflowRecordingRuntime._externalComments.slice()
        
    }
    
    static pluginBridgeSnapshot(): RHWorkflowRecordingPluginBridgeSnapshot {
        
        return {
            isRecording: RHWorkflowRecordingRuntime.isRecording(),
            recording: RHWorkflowRecordingRuntime.recording(),
            savedRecordings: RHWorkflowRecordingRuntime.savedRecordings(),
            externalTimeline: RHWorkflowRecordingRuntime.externalTimeline(),
            externalComments: RHWorkflowRecordingRuntime.externalComments()
        }
        
    }
    
    static saveCurrentRecording(): RHWorkflowRecording {
        
        const recording = RHWorkflowRecordingRuntime.finishRecording()
        RHWorkflowRecordingRuntime.saveRecording(recording)
        RHWorkflowRecordingRuntime._emitRecordingDidChange("saveCurrentRecording")
        return recording
        
    }
    
    static saveRecording(recording: RHWorkflowRecording) {
        
        const recordingToSave = RHWorkflowRecordingRuntime._recordingByEnsuringIdentifier(recording)
        const savedRecordings = RHWorkflowRecordingRuntime.savedRecordings()
        const filteredRecordings = savedRecordings.filter(
            savedRecording => savedRecording.id !== recordingToSave.id
        )
        filteredRecordings.unshift(recordingToSave)
        RHWorkflowRecordingRuntime._writeSavedRecordings(filteredRecordings.slice(0, 50))
        RHWorkflowRecordingRuntime._emitRecordingDidChange("saveRecording")
        
    }
    
    static savedRecordings(): RHWorkflowRecording[] {
        
        try {
            const rawRecordings = window.localStorage.getItem(RHWorkflowRecordingRuntime._savedRecordingsStorageKey)
            if (!rawRecordings) {
                return []
            }
            const parsedRecordings = JSON.parse(rawRecordings)
            if (Array.isArray(parsedRecordings)) {
                return parsedRecordings
            }
        }
        catch (error) {
            console.warn("[RHWorkflowRecordingRuntime] Could not read saved recordings.", error)
        }
        
        return []
        
    }
    
    static deleteSavedRecording(recordingID: string) {
        
        RHWorkflowRecordingRuntime._writeSavedRecordings(
            RHWorkflowRecordingRuntime.savedRecordings().filter(recording => recording.id !== recordingID)
        )
        RHWorkflowRecordingRuntime._emitRecordingDidChange("deleteSavedRecording")
        
    }
    
    static clearSavedRecordings() {
        
        RHWorkflowRecordingRuntime._writeSavedRecordings([])
        RHWorkflowRecordingRuntime._emitRecordingDidChange("clearSavedRecordings")
        
    }
    
    static async saveCurrentRecordingToTestRecordings(): Promise<RHWorkflowRecordingFileSaveResult> {
        
        const recording = RHWorkflowRecordingRuntime.saveCurrentRecording()
        return await RHWorkflowRecordingRuntime.saveRecordingToTestRecordings(recording)
        
    }
    
    static async saveRecordingToTestRecordings(recording: RHWorkflowRecording): Promise<RHWorkflowRecordingFileSaveResult> {
        
        const response = await fetch("/dev/workflow-recordings/test-recordings", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin",
            body: JSON.stringify({
                name: recording.name,
                url: window.location.href,
                recording
            })
        })
        const result = await response.json().catch(() => ({
            success: NO,
            fileName: "",
            relativePath: "",
            message: `File save failed with HTTP ${response.status}.`
        }))
        if (!response.ok) {
            let errorMessage = `File save failed with HTTP ${response.status}.`
            if (typeof result.message === "string" && result.message.length > 0) {
                errorMessage = result.message
            }
            throw new Error(errorMessage)
        }
        
        return result
        
    }
    
    static downloadRecording(): RHWorkflowRecording {
        
        const recording = RHWorkflowRecordingRuntime.finishRecording()
        const blob = new Blob([JSON.stringify(recording, null, 2)], { type: "application/json" })
        const objectURL = URL.createObjectURL(blob)
        const anchorElement = document.createElement("a")
        anchorElement.href = objectURL
        anchorElement.download = `${RHWorkflowRecordingRuntime.fileSafeName(recording.name)}.json`
        anchorElement.click()
        URL.revokeObjectURL(objectURL)
        return recording
        
    }
    
    static isRecording(): boolean {
        
        return RHWorkflowRecordingRuntime._isRecording
        
    }
    
    static metadata(): RHWorkflowRecordingMetadata {
        
        return {
            origin: window.location.origin,
            pathname: window.location.pathname,
            userAgent: window.navigator.userAgent,
            language: window.navigator.language,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight
            },
            timezoneOffsetMinutes: new Date().getTimezoneOffset()
        }
        
    }
    
    static fileSafeName(name: string): string {
        
        const normalizedName = name.trim().replace(/[^A-Za-z0-9._-]+/g, "-")
        if (normalizedName.length > 0) {
            return normalizedName
        }
        return "workflow-recording"
        
    }
    
    static _writeSavedRecordings(recordings: RHWorkflowRecording[]) {
        
        try {
            window.localStorage.setItem(
                RHWorkflowRecordingRuntime._savedRecordingsStorageKey,
                JSON.stringify(recordings)
            )
        }
        catch (error) {
            console.warn("[RHWorkflowRecordingRuntime] Could not save recordings.", error)
        }
        
    }
    
    static _recordingByEnsuringIdentifier(recording: RHWorkflowRecording): RHWorkflowRecording {
        
        if (recording.id && recording.id.length > 0) {
            return recording
        }
        
        return {
            ...recording,
            id: RHWorkflowRecordingRuntime._newRecordingID()
        }
        
    }
    
    static _usageEventWasEmitted(event: RHUsageEvent) {
        
        if (RHWorkflowRecordingRuntime._isRecording !== YES) {
            return
        }
        if (!RHWorkflowRecordingRuntime._eventShouldBeRecorded(event)) {
            return
        }
        
        RHWorkflowRecordingRuntime._recordEvent(event)
        RHWorkflowRecordingRuntime._emitUsageEventWasRecorded(event)
        
    }
    
    static _eventShouldBeRecorded(event: RHUsageEvent): boolean {
        
        if (event.kind !== "socket") {
            if (event.kind === "control") {
                return RHWorkflowRecordingRuntime._controlEventShouldBeRecorded(event)
            }
            return YES
        }
        if (event.functionName === "ButtonWasPressed" || event.functionName === "RouteDidChange") {
            return NO
        }
        
        return YES
        
    }
    
    static _controlEventShouldBeRecorded(event: RHUsageEvent): boolean {
        
        if (!event.eventKey) {
            return YES
        }
        const options = RHWorkflowRecordingRuntime._recordingOptions
        if (
            options.includePointerDetails !== YES &&
            RHWorkflowRecordingRuntime._pointerDetailControlEventKeys.has(event.eventKey)
        ) {
            return NO
        }
        if (
            options.includeFocusEvents !== YES &&
            RHWorkflowRecordingRuntime._focusControlEventKeys.has(event.eventKey)
        ) {
            return NO
        }
        if (
            options.includeKeyboardDetails !== YES &&
            RHWorkflowRecordingRuntime._keyboardDetailControlEventKeys.has(event.eventKey)
        ) {
            return NO
        }
        if (
            options.includeControlDuplicates !== YES &&
            RHWorkflowRecordingRuntime._duplicateControlEventKeys.has(event.eventKey)
        ) {
            return NO
        }
        
        return YES
        
    }
    
    static _recordEvent(event: RHUsageEvent) {
        
        const recordedEvent = RHWorkflowRecordingRuntime._recordableEventFromEvent(event)
        const lastRecordedEvent = RHWorkflowRecordingRuntime._events.lastElement
        if (
            RHWorkflowRecordingRuntime._recordingOptions.includeControlDuplicates !== YES &&
            recordedEvent.kind === "control" &&
            recordedEvent.eventKey === UIView.controlEvent.PrimaryActionTriggered &&
            lastRecordedEvent?.kind === "control" &&
            lastRecordedEvent.eventKey === UIView.controlEvent.PointerUpInside &&
            lastRecordedEvent.target === recordedEvent.target
        ) {
            RHWorkflowRecordingRuntime._events[RHWorkflowRecordingRuntime._events.length - 1] = recordedEvent
            return
        }
        if (
            recordedEvent.kind === "text" &&
            lastRecordedEvent?.kind === "text" &&
            lastRecordedEvent.target === recordedEvent.target
        ) {
            RHWorkflowRecordingRuntime._events[RHWorkflowRecordingRuntime._events.length - 1] = recordedEvent
            return
        }
        
        RHWorkflowRecordingRuntime._events.push(recordedEvent)
        
    }
    
    static _recordableEventFromEvent(event: RHUsageEvent): RHUsageEvent {
        
        const recordedEvent: RHUsageEvent = { ...event }
        if (recordedEvent.kind === "route" && recordedEvent.sanitizedRoute) {
            recordedEvent.route = recordedEvent.sanitizedRoute
        }
        if (recordedEvent.displayText) {
            recordedEvent.displayText = RHWorkflowRecordingRuntime._displayTextForRecording(recordedEvent.displayText)
        }
        delete recordedEvent.sanitizedRoute
        return recordedEvent
        
    }
    
    static _displayTextForRecording(displayText: string): string {
        
        const compactDisplayText = displayText.replace(/\s+/g, " ").trim()
        if (compactDisplayText.length <= RHWorkflowRecordingRuntime._maximumDisplayTextLength) {
            return compactDisplayText
        }
        
        return `${compactDisplayText.slice(0, RHWorkflowRecordingRuntime._maximumDisplayTextLength)}...`
        
    }
    
    static _videoTimeMsForComment(
        comment: Partial<RHWorkflowRecordingExternalComment>,
        createdAtEpochMs: number
    ): number | undefined {
        
        if (typeof comment.videoTimeMs === "number") {
            return comment.videoTimeMs
        }
        
        const timeline = RHWorkflowRecordingRuntime._externalTimeline
        if (timeline?.timeOriginEpochMs) {
            return Math.max(0, createdAtEpochMs - timeline.timeOriginEpochMs)
        }
        if (timeline?.startedAtEpochMs) {
            return Math.max(0, createdAtEpochMs - timeline.startedAtEpochMs)
        }
        
        return undefined
        
    }
    
    static _recordingTimeMsForComment(
        comment: Partial<RHWorkflowRecordingExternalComment>,
        createdAtEpochMs: number
    ): number | undefined {
        
        if (typeof comment.recordingTimeMs === "number") {
            return comment.recordingTimeMs
        }
        if (RHWorkflowRecordingRuntime._startedAt.length === 0) {
            return undefined
        }
        
        const startedAtEpochMs = new Date(RHWorkflowRecordingRuntime._startedAt).getTime()
        if (Number.isNaN(startedAtEpochMs)) {
            return undefined
        }
        
        return Math.max(0, createdAtEpochMs - startedAtEpochMs)
        
    }
    
    static _pluginMessageWasReceived(event: MessageEvent) {
        
        if (event.source !== window) {
            return
        }
        
        const data = event.data
        if (!data || data.source !== "RHWorkflowRecordingPlugin" || data.type !== "command") {
            return
        }
        
        try {
            const result = RHWorkflowRecordingRuntime._resultForPluginCommand(data.command, data.payload)
            RHWorkflowRecordingRuntime._postPluginResponse(data.requestId, YES, result)
        }
        catch (error) {
            let errorMessage = String(error)
            if (error instanceof Error) {
                errorMessage = error.message
            }
            RHWorkflowRecordingRuntime._postPluginResponse(
                data.requestId,
                NO,
                undefined,
                errorMessage
            )
        }
        
    }
    
    static _resultForPluginCommand(command: string, payload: RHWorkflowPluginPayload | undefined): unknown {
        
        if (command === "beginRecording") {
            RHWorkflowRecordingRuntime.beginRecording(payload?.name, payload?.options)
            return RHWorkflowRecordingRuntime.recording()
        }
        if (command === "recordingOptions") {
            return RHWorkflowRecordingRuntime.recordingOptions()
        }
        if (command === "updateRecordingOptions") {
            return RHWorkflowRecordingRuntime.updateRecordingOptions(payload || {})
        }
        if (command === "finishRecording") {
            return RHWorkflowRecordingRuntime.finishRecording()
        }
        if (command === "recording") {
            return RHWorkflowRecordingRuntime.recording()
        }
        if (command === "saveCurrentRecording") {
            return RHWorkflowRecordingRuntime.saveCurrentRecording()
        }
        if (command === "savedRecordings") {
            return RHWorkflowRecordingRuntime.savedRecordings()
        }
        if (command === "deleteSavedRecording") {
            RHWorkflowRecordingRuntime.deleteSavedRecording(payload?.recordingID || payload?.id || "")
            return RHWorkflowRecordingRuntime.savedRecordings()
        }
        if (command === "registerExternalTimeline") {
            return RHWorkflowRecordingRuntime.registerExternalTimeline(payload as RHWorkflowRecordingExternalTimeline)
        }
        if (command === "updateExternalTimeline") {
            return RHWorkflowRecordingRuntime.updateExternalTimeline(payload ?? {})
        }
        if (command === "clearExternalTimeline") {
            RHWorkflowRecordingRuntime.clearExternalTimeline()
            return RHWorkflowRecordingRuntime.pluginBridgeSnapshot()
        }
        if (command === "addExternalComment") {
            return RHWorkflowRecordingRuntime.addExternalComment({ ...payload, text: payload?.text ?? "" })
        }
        if (command === "pluginBridgeSnapshot") {
            return RHWorkflowRecordingRuntime.pluginBridgeSnapshot()
        }
        
        throw new Error(`Unknown RH workflow recording plugin command "${command}".`)
        
    }
    
    static _postPluginResponse<Result>(requestId: string, ok: boolean, result?: Result, error?: string) {
        
        window.postMessage({
            source: "RHWorkflowRecordingRuntime",
            type: "response",
            requestId,
            ok,
            result,
            error
        }, "*")
        
    }
    
    static _emitRecordingDidChange(reason: string) {
        
        const detail = {
            reason,
            snapshot: RHWorkflowRecordingRuntime.pluginBridgeSnapshot()
        }
        window.dispatchEvent(new CustomEvent("RHWorkflowRecordingRuntime:recordingDidChange", { detail }))
        window.postMessage({
            source: "RHWorkflowRecordingRuntime",
            type: "recordingDidChange",
            detail
        }, "*")
        
    }
    
    static _emitUsageEventWasRecorded(event: RHUsageEvent) {
        
        const detail = {
            event,
            snapshot: RHWorkflowRecordingRuntime.pluginBridgeSnapshot()
        }
        window.dispatchEvent(new CustomEvent("RHWorkflowRecordingRuntime:eventRecorded", { detail }))
        window.postMessage({
            source: "RHWorkflowRecordingRuntime",
            type: "eventRecorded",
            detail
        }, "*")
        
    }
    
    static _beginRecordingFromURLIfNeeded() {
        
        const parameters = RHWorkflowRecordingRuntime._recordingParameters()
        const recordingParameter = parameters.get("rh-recording")
        if (recordingParameter !== "1" && recordingParameter !== "start") {
            return
        }
        
        RHWorkflowRecordingRuntime.beginRecording(parameters.get("rh-recording-name") || undefined)
        
    }
    
    static _recordingParameters(): URLSearchParams {
        
        const parameters = new URLSearchParams(window.location.search)
        const hash = window.location.hash
        const queryStartIndex = hash.indexOf("?")
        if (queryStartIndex >= 0) {
            const hashParameters = new URLSearchParams(hash.slice(queryStartIndex + 1))
            hashParameters.forEach((value, key) => parameters.set(key, value))
        }
        
        return parameters
        
    }
    
    static _recordingNameFromName(name?: string): string {
        
        if (name && name.trim().length > 0) {
            return name.trim()
        }
        
        return `workflow-${new Date().toISOString().replace(/[:.]/g, "-")}`
        
    }
    
    static _newRecordingID(): string {
        
        return `recording-${Date.now()}-${Math.round(Math.random() * 1000000)}`
        
    }
    
}
